'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { createClient } from '@/lib/supabase/client';
import { retryQuery } from '@/lib/retryQuery';

const AuthContext = createContext<any>({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

/** Normalise Supabase auth error messages into user-friendly strings */
function normalizeAuthError(err: any): string {
  const msg: string = err?.message || String(err) || 'An unexpected error occurred';
  if (msg.includes('Invalid login credentials')) return 'Invalid email or password. Please try again.';
  if (msg.includes('Email not confirmed')) return 'Please verify your email address before signing in.';
  if (msg.includes('User already registered')) return 'An account with this email already exists.';
  if (msg.includes('Password should be')) return 'Password must be at least 6 characters.';
  if (msg.includes('rate limit') || msg.includes('too many requests')) return 'Too many attempts. Please wait a moment and try again.';
  if (msg.includes('network') || msg.includes('fetch')) return 'Network error. Please check your connection and try again.';
  return msg;
}

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);
  const supabase = createClient();

  useEffect(() => {
    // Get initial session with retry
    retryQuery(
      () => supabase.auth.getSession().then(({ data, error }) => {
        if (error) throw error;
        return data.session;
      }),
      {
        maxAttempts: 3,
        baseDelayMs: 400,
        onRetry: (attempt, err) => console.warn(`[AuthContext] getSession retry ${attempt}:`, err.message),
      }
    )
      .then((sess) => {
        setSession(sess);
        setUser(sess?.user ?? null);
        setAuthError(null);
      })
      .catch((err) => {
        console.error('[AuthContext] Failed to restore session:', err);
        setAuthError(normalizeAuthError(err));
      })
      .finally(() => setLoading(false));

    // Listen for auth changes
    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange((_event, sess) => {
      setSession(sess);
      setUser(sess?.user ?? null);
      setLoading(false);
      setAuthError(null);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Email/Password Sign Up
  const signUp = async (email: string, password: string, metadata: any = {}) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: metadata?.fullName || '',
            avatar_url: metadata?.avatarUrl || ''
          },
          emailRedirectTo: `${window.location.origin}/auth/callback`
        }
      });
      if (error) throw error;
      setAuthError(null);
      return data;
    } catch (err: any) {
      const msg = normalizeAuthError(err);
      setAuthError(msg);
      throw new Error(msg);
    }
  };

  // Email/Password Sign In — with retry on network errors only
  const signIn = async (email: string, password: string) => {
    try {
      const data = await retryQuery(
        async () => {
          const { data, error } = await supabase.auth.signInWithPassword({ email, password });
          if (error) throw error;
          return data;
        },
        {
          maxAttempts: 2,
          baseDelayMs: 600,
          onRetry: (attempt, err) => {
            // Only retry on network-level errors, not credential errors
            const isNetworkError = err.message.includes('network') || err.message.includes('fetch');
            if (!isNetworkError) throw err; // abort retry for auth errors
            console.warn(`[AuthContext] signIn retry ${attempt}:`, err.message);
          },
        }
      );
      setAuthError(null);
      return data;
    } catch (err: any) {
      const msg = normalizeAuthError(err);
      setAuthError(msg);
      throw new Error(msg);
    }
  };

  // Sign Out
  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      setAuthError(null);
    } catch (err: any) {
      const msg = normalizeAuthError(err);
      setAuthError(msg);
      throw new Error(msg);
    }
  };

  // Get Current User with retry
  const getCurrentUser = async () => {
    try {
      return await retryQuery(async () => {
        const { data: { user }, error } = await supabase.auth.getUser();
        if (error) throw error;
        return user;
      }, { maxAttempts: 2 });
    } catch (err: any) {
      const msg = normalizeAuthError(err);
      setAuthError(msg);
      throw new Error(msg);
    }
  };

  // Check if Email is Verified
  const isEmailVerified = () => {
    return user?.email_confirmed_at !== null;
  };

  // Get User Profile from Database with retry
  const getUserProfile = async () => {
    if (!user) return null;
    try {
      return await retryQuery(async () => {
        const { data, error } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('id', user.id)
          .single();
        if (error) throw error;
        return data;
      }, { maxAttempts: 2 });
    } catch (err: any) {
      const msg = normalizeAuthError(err);
      setAuthError(msg);
      throw new Error(msg);
    }
  };

  const value = {
    user,
    session,
    loading,
    authError,
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
